import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AudioPlayer = ({ verse, className = '' }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [audioError, setAudioError] = useState(false);
  const audioRef = useRef(null);

  // Mock audio URL - in real app, this would come from an audio service
  const audioUrl = `https://audio-bible.com/verse/${verse?.book}/${verse?.chapter}/${verse?.verse}`;

  useEffect(() => {
    const audio = audioRef?.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio?.currentTime);
    const updateDuration = () => setDuration(audio?.duration);
    const handleEnded = () => setIsPlaying(false);
    const handleError = () => {
      setAudioError(true);
      setIsPlaying(false);
    };

    audio?.addEventListener('timeupdate', updateTime);
    audio?.addEventListener('loadedmetadata', updateDuration);
    audio?.addEventListener('ended', handleEnded);
    audio?.addEventListener('error', handleError);

    return () => {
      audio?.removeEventListener('timeupdate', updateTime);
      audio?.removeEventListener('loadedmetadata', updateDuration);
      audio?.removeEventListener('ended', handleEnded);
      audio?.removeEventListener('error', handleError);
    };
  }, []);

  const togglePlayPause = () => {
    const audio = audioRef?.current;
    if (!audio || audioError) return;

    if (isPlaying) {
      audio?.pause();
    } else {
      audio?.play()?.catch(() => {
        setAudioError(true);
        setIsPlaying(false);
      });
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e) => {
    const audio = audioRef?.current;
    if (!audio) return;

    const rect = e?.currentTarget?.getBoundingClientRect();
    const percent = (e?.clientX - rect?.left) / rect?.width;
    const newTime = percent * duration;
    
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const changePlaybackRate = () => {
    const rates = [0.5, 0.75, 1, 1.25, 1.5];
    const currentIndex = rates?.indexOf(playbackRate);
    const nextRate = rates?.[(currentIndex + 1) % rates?.length];
    
    setPlaybackRate(nextRate);
    if (audioRef?.current) {
      audioRef.current.playbackRate = nextRate;
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds?.toString()?.padStart(2, '0')}`;
  };

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  // If audio source is unavailable, show a message instead
  if (audioError) {
    return (
      <div className={`bg-card rounded-lg p-4 shadow-warm ${className}`}>
        <div className="flex items-center justify-center space-x-2 text-muted-foreground">
          <Icon name="Volume2" size={18} />
          <span className="text-sm">Audio not available for this verse</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-card rounded-lg p-4 shadow-warm ${className}`}>
      <audio
        ref={audioRef}
        src={audioUrl}
        preload="none"
      />
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon name="Volume2" size={18} color="var(--color-primary)" />
            <span className="text-sm font-medium text-foreground">Audio Recitation</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={changePlaybackRate}
            className="text-xs"
          >
            {playbackRate}x
          </Button>
        </div>

        {/* Progress Bar */}
        <div className="space-y-2">
          <div
            className="w-full bg-muted rounded-full h-2 cursor-pointer"
            onClick={handleSeek}
          >
            <div
              className="bg-primary h-2 rounded-full transition-all duration-100"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          
          <div className="flex items-center justify-between text-xs text-muted-foreground font-mono">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center space-x-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              if (audioRef?.current) {
                audioRef.current.currentTime = Math.max(0, currentTime - 10);
              }
            }}
            title="Rewind 10 seconds"
          >
            <Icon name="RotateCcw" size={18} />
          </Button>

          <Button
            variant="default"
            size="icon"
            onClick={togglePlayPause}
            className="w-12 h-12"
          >
            <Icon name={isPlaying ? 'Pause' : 'Play'} size={20} />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              if (audioRef?.current) {
                audioRef.current.currentTime = Math.min(duration, currentTime + 10);
              }
            }}
            title="Forward 10 seconds"
          >
            <Icon name="RotateCw" size={18} />
          </Button>
        </div>

        {/* Verse Reference */}
        <div className="text-center">
          <p className="text-sm font-caption text-muted-foreground">
            {verse?.reference} - {verse?.version || 'KJV'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;